import { isTaggedTouch, PLUS_ATTRIBUTION_KEYS, type PlusAttribution } from "@shared/plus-attribution";

export type LeadAttribution = {
  pageUrl?: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
};

const STORAGE_KEY = "adv_landing_attribution";

type Landing = Omit<LeadAttribution, "pageUrl">;

function externalReferrer(): string | undefined {
  const ref = document.referrer;
  if (!ref) return undefined;
  try {
    return new URL(ref).host === window.location.host ? undefined : ref;
  } catch {
    return undefined;
  }
}

function readStored(): Landing {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Landing) : {};
  } catch {
    return {};
  }
}

// Called once on app load: UTMs live only on the landing URL, so an SPA
// navigation before the form submit would otherwise lose them.
export function rememberLandingAttribution(): void {
  if (typeof window === "undefined") return;
  rememberPaidTouch();
  rememberGoogleClick();
  const sp = new URLSearchParams(window.location.search);
  const fromUrl: Landing = {
    utmSource: sp.get("utm_source") || undefined,
    utmMedium: sp.get("utm_medium") || undefined,
    utmCampaign: sp.get("utm_campaign") || undefined,
  };
  const stored = readStored();
  const next: Landing = {
    referrer: stored.referrer ?? externalReferrer(),
    ...(fromUrl.utmSource || fromUrl.utmMedium || fromUrl.utmCampaign
      ? fromUrl
      : { utmSource: stored.utmSource, utmMedium: stored.utmMedium, utmCampaign: stored.utmCampaign }),
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private mode / blocked storage: attribution is best-effort.
  }
}

export function getLeadAttribution(): LeadAttribution {
  if (typeof window === "undefined") return {};
  const stored = readStored();
  return {
    pageUrl: window.location.href.slice(0, 2000),
    referrer: (stored.referrer ?? externalReferrer())?.slice(0, 2000),
    utmSource: stored.utmSource,
    utmMedium: stored.utmMedium,
    utmCampaign: stored.utmCampaign,
  };
}

/* ─── Plus signup source (2026-10-16) ─────────────────────────────────────────
   The lead fields above live in sessionStorage and only carry three UTMs. A
   Plus signup can come days after the ad click, so the signup source is kept
   separately: localStorage, 30 days, every UTM plus Google's click ids.
   The most recent TAGGED touch wins; an untagged visit never replaces it. With
   no tagged touch on file, the latest external referrer is kept, and a direct
   visit is recorded only when nothing else is. Sent with POST /api/pro/checkout
   (shared/plus-attribution.ts has the server side). */

const PAID_TOUCH_KEY = "adv_plus_touch";
const PAID_TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Fallback when storage is blocked: the touch from this page load. */
let memoryTouch: PlusAttribution | null = null;

function stripQuery(url: string): string | undefined {
  try {
    const u = new URL(url);
    return `${u.origin}${u.pathname}`;
  } catch {
    return undefined;
  }
}

function hostOfUrl(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return "";
  }
}

function readPaidTouch(): PlusAttribution | null {
  try {
    const raw = localStorage.getItem(PAID_TOUCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PlusAttribution;
    const at = parsed?.touched_at ? Date.parse(parsed.touched_at) : NaN;
    if (!Number.isFinite(at) || Date.now() - at > PAID_TOUCH_TTL_MS) {
      localStorage.removeItem(PAID_TOUCH_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function rememberPaidTouch(): void {
  const sp = new URLSearchParams(window.location.search);
  const ref = externalReferrer();
  const current: PlusAttribution = { landing_path: window.location.pathname, touched_at: new Date().toISOString() };
  for (const key of PLUS_ATTRIBUTION_KEYS) {
    if (key === "landing_path" || key === "referrer" || key === "touched_at") continue;
    const v = sp.get(key)?.trim();
    if (v) current[key] = v;
  }
  // A return from Stripe Checkout is not a new source.
  if (ref && !/(^|\.)stripe\.com$/.test(hostOfUrl(ref))) current.referrer = stripQuery(ref);

  const stored = readPaidTouch();
  const replace = isTaggedTouch(current) || !stored || (!isTaggedTouch(stored) && !!current.referrer);
  memoryTouch = replace ? current : stored;
  if (!replace) return;
  try {
    localStorage.setItem(PAID_TOUCH_KEY, JSON.stringify(current));
  } catch {
    // Private mode / blocked storage: memoryTouch still covers this page load.
  }
}

/** The touch to credit a Plus signup to, or null when nothing was recorded. */
export function getPlusAttribution(): PlusAttribution | null {
  if (typeof window === "undefined") return null;
  return readPaidTouch() ?? memoryTouch;
}

/* ─── Google click ids for lead forms ────────────────────────────────────────
   Sent with /api/contact so the server can put the gclid on the HubSpot contact
   for offline conversions. Kept apart from the Plus touch above: that one is
   replaced by ANY later tagged visit, so a Google click followed by a Meta or
   email link would lose the gclid. Here only a newer Google click replaces it.
   The 90-day TTL matches Google Ads' click-conversion window; Safari (ITP) may
   still clear script storage after 7 days without a visit. */

type AdClickIds = Pick<PlusAttribution, "gclid" | "gbraid" | "wbraid">;

const GOOGLE_CLICK_KEY = "adv_google_click";
const GOOGLE_CLICK_TTL_MS = 90 * 24 * 60 * 60 * 1000;

let memoryGoogleClick: AdClickIds | null = null;

function rememberGoogleClick(): void {
  const sp = new URLSearchParams(window.location.search);
  const ids: AdClickIds = {};
  for (const key of ["gclid", "gbraid", "wbraid"] as const) {
    const v = sp.get(key)?.trim().slice(0, 500);
    if (v) ids[key] = v;
  }
  // Only a new gclid replaces the stored click: the server forwards the gclid
  // alone, so a gbraid/wbraid-only click (iOS) must not drop the one on file.
  if (!ids.gclid) return;
  memoryGoogleClick = ids;
  try {
    localStorage.setItem(GOOGLE_CLICK_KEY, JSON.stringify({ ...ids, touched_at: new Date().toISOString() }));
  } catch {
    // Private mode / blocked storage: memoryGoogleClick still covers this page load.
  }
}

export function getAdClickIds(): AdClickIds {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(GOOGLE_CLICK_KEY);
    if (raw) {
      const { touched_at, gclid, gbraid, wbraid } = JSON.parse(raw) as AdClickIds & { touched_at?: string };
      const at = touched_at ? Date.parse(touched_at) : NaN;
      if (Number.isFinite(at) && Date.now() - at <= GOOGLE_CLICK_TTL_MS) {
        return { ...(gclid ? { gclid } : {}), ...(gbraid ? { gbraid } : {}), ...(wbraid ? { wbraid } : {}) };
      }
      localStorage.removeItem(GOOGLE_CLICK_KEY);
    }
  } catch {
    // Unreadable storage: fall through to this page load's click, if any.
  }
  return memoryGoogleClick ?? {};
}
